/*
  Project Structure tab — pure helpers (UI overhaul wave 2, screen 08). No React, no API.
  Unit-tested in src/__tests__/project-detail-w2b-model.test.js.

  - Collapse keys of the tree rows (moved here from ProjectDetailPage so search can open a result's parents)
  - diffStructure(saved, current)      → what "Save structure" would change: count + summary (08 #4)
  - buildStructureIndex(structure)     → every building / floor / room / panel / device with its path (08 #5)
  - searchStructureIndex(index, query) → matches on name, address or part number, best first
  - summarizeImportPreview(preview)    → "+12 devices · −2 panels" for the Import wizard (08 #10)
*/

const text = (value) => String(value ?? '').trim();
const plural = (count, word, many = `${word}s`) => `${count} ${count === 1 ? word : many}`;
const listOf = (node, key) => (Array.isArray(node?.[key]) ? node[key].filter((item) => item && typeof item === 'object') : []);
const childNodesOf = (node) => (Array.isArray(node?.children) ? listOf(node, 'children') : listOf(node, 'devices'));

/* ------------------------------------------------------------------ collapse keys */

const resolveIds = (roomOrPanelId = null, panelId) => (
  panelId === undefined ? { roomId: null, panelId: roomOrPanelId } : { roomId: roomOrPanelId, panelId }
);
export const getBuildingCollapseKey = (buildingId) => `building::${buildingId}`;
export const getFloorCollapseKey = (buildingId, floorId) => `floor::${buildingId}::${floorId}`;
export const getRoomCollapseKey = (buildingId, floorId, roomId) => `room::${buildingId}::${floorId}::${roomId}`;
export const getBmsSubCollapseKey = (buildingId, floorId, path = []) => `bms-sub::${buildingId}::${floorId}::${path.join('::')}`;
export const getPanelCollapseKey = (buildingId, floorId, roomOrPanelId, panelId) => {
  const { roomId, panelId: resolvedPanelId } = resolveIds(roomOrPanelId, panelId);
  return `panel::${buildingId}::${floorId}::${roomId || 'floor'}::${resolvedPanelId}`;
};

/* ------------------------------------------------------------------ walking the tree */

const isZone = (panel) => {
  const type = text(panel?.type).toLowerCase();
  return type ? type === 'zone' : text(panel?.name).toLowerCase().startsWith('zone');
};

/**
 * Calls visit(entry) for every node, parents first. entry = { node, id, kind, name, parentId, index,
 * path (ancestor names), collapseKeys (keys that must be open to see this node), buildingId, floorId }.
 */
export function walkStructure(structure = {}, visit, { isBms = false } = {}) {
  const walkDevices = (devices, ctx) => {
    devices.forEach((device, index) => {
      const nodePath = [...ctx.nodePath, device.id];
      const name = text(device.name) || text(device.part_number) || text(device.address) || 'Device';
      visit({
        node: device,
        id: device.id,
        kind: isBms ? 'sub-item' : 'device',
        name,
        parentId: ctx.parentId,
        index,
        path: ctx.path,
        collapseKeys: ctx.collapseKeys,
        buildingId: ctx.buildingId,
        floorId: ctx.floorId,
      });
      const children = childNodesOf(device);
      if (children.length) {
        walkDevices(children, {
          ...ctx,
          parentId: device.id,
          nodePath,
          path: [...ctx.path, name],
          collapseKeys: isBms ? [...ctx.collapseKeys, getBmsSubCollapseKey(ctx.buildingId, ctx.floorId, nodePath)] : ctx.collapseKeys,
        });
      }
    });
  };

  listOf(structure, 'buildings').forEach((building, buildingIndex) => {
    const bName = text(building.name) || 'Building';
    const bKeys = [getBuildingCollapseKey(building.id)];
    visit({ node: building, id: building.id, kind: 'building', name: bName, parentId: null, index: buildingIndex, path: [], collapseKeys: [], buildingId: building.id, floorId: null });
    listOf(building, 'floors').forEach((floor, floorIndex) => {
      const fName = text(floor.name) || (isBms ? 'Sub' : 'Floor');
      const fKeys = [...bKeys, getFloorCollapseKey(building.id, floor.id)];
      const fPath = [bName, fName];
      visit({ node: floor, id: floor.id, kind: isBms ? 'sub' : 'floor', name: fName, parentId: building.id, index: floorIndex, path: [bName], collapseKeys: bKeys, buildingId: building.id, floorId: floor.id });
      const base = { buildingId: building.id, floorId: floor.id, nodePath: [] };
      walkDevices(listOf(floor, 'devices'), { ...base, parentId: floor.id, path: fPath, collapseKeys: fKeys });
      listOf(floor, 'panels').forEach((panel, panelIndex) => {
        const pName = text(panel.name) || (isZone(panel) ? 'Zone' : 'Panel');
        const pKeys = [...fKeys, getPanelCollapseKey(building.id, floor.id, panel.id)];
        visit({ node: panel, id: panel.id, kind: isZone(panel) ? 'zone' : 'panel', name: pName, parentId: floor.id, index: panelIndex, path: fPath, collapseKeys: fKeys, buildingId: building.id, floorId: floor.id });
        walkDevices(listOf(panel, 'devices'), { ...base, parentId: panel.id, path: [...fPath, pName], collapseKeys: pKeys });
      });
      listOf(floor, 'rooms').forEach((room, roomIndex) => {
        const rName = text(room.name) || (isBms ? 'Zone' : 'Room');
        const rKeys = [...fKeys, getRoomCollapseKey(building.id, floor.id, room.id)];
        const rPath = [...fPath, rName];
        visit({ node: room, id: room.id, kind: isBms ? 'zone' : 'room', name: rName, parentId: floor.id, index: roomIndex, path: fPath, collapseKeys: fKeys, buildingId: building.id, floorId: floor.id });
        walkDevices(listOf(room, 'devices'), { ...base, parentId: room.id, path: rPath, collapseKeys: rKeys });
        listOf(room, 'panels').forEach((panel, panelIndex) => {
          const pName = text(panel.name) || 'Panel';
          const pKeys = [...rKeys, getPanelCollapseKey(building.id, floor.id, room.id, panel.id)];
          visit({ node: panel, id: panel.id, kind: 'panel', name: pName, parentId: room.id, index: panelIndex, path: rPath, collapseKeys: rKeys, buildingId: building.id, floorId: floor.id });
          walkDevices(listOf(panel, 'devices'), { ...base, parentId: panel.id, path: [...rPath, pName], collapseKeys: pKeys });
        });
      });
    });
  });
}

/* ------------------------------------------------------------------ diff (08 #4) */

// Fields that are not part of what a person edits (lists are compared through the walk).
const IGNORED_FIELDS = new Set(['id', 'name', 'floors', 'rooms', 'panels', 'devices', 'children', 'updated', 'updated_at']);
const comparable = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value).trim();
};
function changedFields(before, after) {
  const keys = new Set([...Object.keys(before || {}), ...Object.keys(after || {})]);
  return [...keys].filter((key) => !IGNORED_FIELDS.has(key) && comparable(before?.[key]) !== comparable(after?.[key]));
}

const KIND_WORDS = {
  building: ['building', 'buildings'],
  floor: ['floor', 'floors'],
  sub: ['sub', 'subs'],
  room: ['room', 'rooms'],
  zone: ['zone', 'zones'],
  panel: ['panel', 'panels'],
  device: ['device', 'devices'],
  'sub-item': ['sub-item', 'sub-items'],
};
const kindCount = (kind, count) => {
  const [one, many] = KIND_WORDS[kind] || [kind, `${kind}s`];
  return plural(count, one, many);
};

function snapshot(structure, isBms) {
  const map = new Map();
  walkStructure(structure, (entry) => {
    if (!entry.id) return;
    map.set(entry.id, entry);
  }, { isBms });
  return map;
}

function countDescendantDevices(node) {
  let count = 0;
  const visitDevices = (devices) => devices.forEach((device) => { count += 1; visitDevices(childNodesOf(device)); });
  visitDevices(listOf(node, 'devices').concat(Array.isArray(node?.children) ? listOf(node, 'children') : []));
  listOf(node, 'floors').forEach((floor) => { count += countDescendantDevices(floor); });
  listOf(node, 'rooms').forEach((room) => { count += countDescendantDevices(room); });
  listOf(node, 'panels').forEach((panel) => { count += countDescendantDevices(panel); });
  return count;
}

/**
 * What saving would change, compared with the saved structure:
 *   { count, changes: [{ type, kind, name, path, detail }], summary }
 * An added (or removed) building counts once, with what is inside it in `detail`; a reorder counts once
 * per list. `count` is what the save bar, the Save button and the tab bar show.
 */
export function diffStructure(saved = {}, current = {}, { isBms = false } = {}) {
  const before = snapshot(saved, isBms);
  const after = snapshot(current, isBms);
  const changes = [];
  const addedIds = new Set([...after.keys()].filter((id) => !before.has(id)));
  const removedIds = new Set([...before.keys()].filter((id) => !after.has(id)));

  const describeSubtree = (entry) => {
    const devices = countDescendantDevices(entry.node);
    return devices && !['device', 'sub-item'].includes(entry.kind) ? `with ${kindCount(isBms ? 'sub-item' : 'device', devices)}` : '';
  };
  after.forEach((entry, id) => {
    if (!addedIds.has(id) || addedIds.has(entry.parentId)) return;
    changes.push({ type: 'added', kind: entry.kind, name: entry.name, path: entry.path, detail: describeSubtree(entry), id, parentId: entry.parentId ?? null });
  });
  before.forEach((entry, id) => {
    if (!removedIds.has(id) || removedIds.has(entry.parentId)) return;
    changes.push({ type: 'removed', kind: entry.kind, name: entry.name, path: entry.path, detail: describeSubtree(entry), id, parentId: entry.parentId ?? null });
  });

  const stayed = new Set();
  after.forEach((entry, id) => {
    const previous = before.get(id);
    if (!previous) return;
    if (text(previous.node.name) !== text(entry.node.name)) {
      changes.push({ type: 'renamed', kind: entry.kind, name: entry.name, path: entry.path, detail: `${text(previous.node.name) || '—'} → ${text(entry.node.name) || '—'}`, id, parentId: entry.parentId ?? null });
    }
    const fields = changedFields(previous.node, entry.node);
    if (fields.length) {
      changes.push({ type: 'edited', kind: entry.kind, name: entry.name, path: entry.path, detail: fields.join(', '), id, parentId: entry.parentId ?? null });
    }
    if (previous.parentId !== entry.parentId) {
      changes.push({ type: 'moved', kind: entry.kind, name: entry.name, path: entry.path, detail: `from ${previous.path.join(' › ') || 'top level'}`, id, parentId: entry.parentId ?? null, fromParentId: previous.parentId ?? null });
    } else {
      stayed.add(id);
    }
  });
  // Same parent, different order of the nodes that stayed under it → one "reordered" per parent.
  const ROOT = '__root__';
  const orderByParent = (map) => {
    const order = new Map();
    map.forEach((entry, id) => {
      if (!stayed.has(id)) return;
      const key = entry.parentId ?? ROOT;
      if (!order.has(key)) order.set(key, []);
      order.get(key).push(id);
    });
    return order;
  };
  const wasOrder = orderByParent(before);
  orderByParent(after).forEach((ids, parentKey) => {
    if ((wasOrder.get(parentKey) || []).join('|') === ids.join('|')) return;
    const parent = parentKey === ROOT ? null : after.get(parentKey);
    changes.push({ type: 'reordered', kind: parent?.kind || 'building', name: parent?.name || 'Buildings', path: parent?.path || [], detail: '', id: parentKey === ROOT ? null : parentKey, parentId: parent?.parentId ?? null });
  });

  return { count: changes.length, changes, summary: summarizeChanges(changes) };
}

const KIND_LABEL = (kind) => (KIND_WORDS[kind] ? KIND_WORDS[kind][0] : kind);
const where = (change) => (change.path?.length ? ` in ${change.path.join(' › ')}` : '');

/** One change in words: "Renamed building Penthouse 02 → Penthouse 2", "Added device 1.1.20 in Villa 9 › GF". */
export function describeChange(change) {
  const kind = KIND_LABEL(change.kind);
  switch (change.type) {
    case 'added': return `Added ${kind} ${change.name}${change.detail ? ` (${change.detail})` : ''}${where(change)}`;
    case 'removed': return `Removed ${kind} ${change.name}${change.detail ? ` (${change.detail})` : ''}${where(change)}`;
    case 'renamed': return `Renamed ${kind} ${change.detail}`;
    case 'edited': return `Edited ${kind} ${change.name}: ${change.detail.replace(/_/g, ' ')}`;
    case 'moved': return `Moved ${kind} ${change.name} ${change.detail}`;
    default: return `Reordered ${change.name === 'Buildings' ? 'buildings' : `in ${change.name}`}`;
  }
}

/** "+3 devices · 1 renamed · 2 removed" (one or two changes are spelled out instead). */
export function summarizeChanges(changes = []) {
  if (!changes.length) return '';
  if (changes.length <= 2) return changes.map(describeChange).join(' · ');
  const added = {};
  const removed = {};
  let renamed = 0;
  let edited = 0;
  let moved = 0;
  changes.forEach((change) => {
    if (change.type === 'added') added[change.kind] = (added[change.kind] || 0) + 1;
    else if (change.type === 'removed') removed[change.kind] = (removed[change.kind] || 0) + 1;
    else if (change.type === 'renamed') renamed += 1;
    else if (change.type === 'edited') edited += 1;
    else moved += 1;
  });
  const parts = [];
  const addedText = Object.entries(added).map(([kind, count]) => `+${kindCount(kind, count)}`).join(', ');
  const removedText = Object.entries(removed).map(([kind, count]) => `−${kindCount(kind, count)}`).join(', ');
  if (addedText) parts.push(addedText);
  if (removedText) parts.push(removedText);
  if (renamed) parts.push(`${renamed} renamed`);
  if (edited) parts.push(`${edited} edited`);
  if (moved) parts.push(`${moved} moved`);
  return parts.join(' · ');
}

/* ------------------------------------------------------------------ search (08 #5) */

/** Flat, searchable list of every node: { id, kind, name, partNumber, address, path, collapseKeys, haystack }. */
export function buildStructureIndex(structure = {}, { isBms = false } = {}) {
  const index = [];
  walkStructure(structure, (entry) => {
    const partNumber = text(entry.node.part_number);
    const address = text(entry.node.address);
    index.push({
      id: entry.id,
      kind: entry.kind,
      name: entry.name,
      partNumber,
      address,
      zoneName: text(entry.node.zone_name),
      deviceType: text(entry.node.device_type),
      path: entry.path,
      collapseKeys: entry.collapseKeys,
      buildingId: entry.buildingId,
      parentId: entry.parentId, // wave 3 (08 #1): a device opens its container in the two-pane editor
      haystack: [entry.name, partNumber, address, entry.node.zone_name, entry.node.device_type].map((value) => text(value).toLowerCase()).filter(Boolean),
    });
  }, { isBms });
  return index;
}

/**
 * Matches for `query` (case-insensitive), best first: exact address / part number, then a field that starts
 * with the query, then contains it. → { total, results: [{ ...entry, matchedOn }] }
 */
export function searchStructureIndex(index = [], query = '', { limit = 50 } = {}) {
  const needle = text(query).toLowerCase();
  if (!needle) return { total: 0, results: [] };
  const scored = [];
  index.forEach((entry, position) => {
    const address = entry.address.toLowerCase();
    const part = entry.partNumber.toLowerCase();
    let score = null;
    let matchedOn = 'name';
    if (address && address === needle) { score = 0; matchedOn = 'address'; }
    else if (part && part === needle) { score = 0; matchedOn = 'part'; }
    else if (address.startsWith(needle)) { score = 1; matchedOn = 'address'; }
    else if (part.startsWith(needle)) { score = 1; matchedOn = 'part'; }
    else if (entry.haystack.some((value) => value.startsWith(needle))) score = 2;
    else if (entry.haystack.some((value) => value.includes(needle))) {
      score = 3;
      if (address.includes(needle)) matchedOn = 'address';
      else if (part.includes(needle)) matchedOn = 'part';
    }
    if (score !== null) scored.push({ entry, score, position, matchedOn });
  });
  scored.sort((a, b) => a.score - b.score || a.position - b.position);
  return {
    total: scored.length,
    results: scored.slice(0, limit).map(({ entry, matchedOn }) => ({ ...entry, matchedOn })),
  };
}

/** Duplicate device addresses: address → [entries] (only addresses used more than once). */
export function findDuplicateAddresses(index = []) {
  const byAddress = new Map();
  index.forEach((entry) => {
    if (!entry.address || !['device', 'sub-item'].includes(entry.kind)) return;
    if (!byAddress.has(entry.address)) byAddress.set(entry.address, []);
    byAddress.get(entry.address).push(entry);
  });
  return new Map([...byAddress].filter(([, entries]) => entries.length > 1));
}

/* ------------------------------------------------------------------ import preview (08 #10) */

const LEVELS = [
  ['buildings', 'building'],
  ['floors', 'floor'],
  ['rooms', 'room'],
  ['panels', 'panel'],
  ['devices', 'device'],
];

/** "+12 devices · −2 panels" (or "No changes") from an import-preview response. */
export function summarizeImportPreview(preview) {
  if (!preview) return '';
  const parts = [];
  LEVELS.forEach(([key, kind]) => {
    const added = Number(preview.added?.[key]?.count || 0);
    if (added) parts.push(`+${kindCount(kind, added)}`);
  });
  LEVELS.forEach(([key, kind]) => {
    const removed = Number(preview.removed?.[key]?.count || 0);
    if (removed) parts.push(`−${kindCount(kind, removed)}`);
  });
  return parts.length ? parts.join(' · ') : 'No changes';
}

export const IMPORT_LEVELS = LEVELS.map(([key, kind]) => ({ key, label: KIND_WORDS[kind][1].replace(/^./, (c) => c.toUpperCase()) }));

/** Spreadsheet column letter for a 0-based index: 0 → A, 26 → AA. */
export function columnLetter(index) {
  let value = Number(index);
  if (!Number.isInteger(value) || value < 0) return '';
  let letters = '';
  value += 1;
  while (value > 0) {
    const remainder = (value - 1) % 26;
    letters = String.fromCharCode(65 + remainder) + letters;
    value = Math.floor((value - 1) / 26);
  }
  return letters;
}

export const IMPORT_MAPPING_FIELDS = [
  { key: 'label', label: 'Locations & device addresses', required: true, hint: 'Buildings, floors and rooms (indented) and addresses like 1.1.12' },
  { key: 'part_number', label: 'Part number' },
  { key: 'product', label: 'Product (device name)' },
  { key: 'application', label: 'Application (name if no product)' },
];

/* ------------------------------------------------------------------ save impact (wave 3, 08 #8) */

/**
 * The dry run of "Save structure" (POST …/execution-structure/preview) in a few words for the save bar:
 * "On save: +3 sub-items · Devices Installation, Pre-Programming" · "On save: −22 sub-items (3 with work) · Shop
 * Drawings" — totals first (they fit the bar), then the tasks they land in.
 */
export function describeStructureImpact(impact) {
  if (!impact || impact.enabled === false) return '';
  const tasks = Array.isArray(impact.tasks) ? impact.tasks : [];
  const changed = tasks.filter((entry) => entry.added || entry.removed);
  if (!changed.length) return impact.blocked ? `Save is refused: ${impact.blocked}` : 'On save: no change to Execution';
  const added = changed.reduce((sum, entry) => sum + (Number(entry.added) || 0), 0);
  const removed = changed.reduce((sum, entry) => sum + (Number(entry.removed) || 0), 0);
  const withWork = changed.reduce((sum, entry) => sum + (Number(entry.removed_with_work) || 0), 0);
  const total = added && removed ? `+${added} / −${removed}` : added ? `+${added}` : `−${removed}`;
  const noun = `sub-item${(added || removed) === 1 && !(added && removed) ? '' : 's'}`;
  const names = changed.slice(0, 3).map((entry) => entry.task).join(', ') + (changed.length > 3 ? ` +${changed.length - 3} more` : '');
  const blocked = impact.blocked ? ` · save is refused: ${impact.blocked}` : '';
  return `On save: ${total} ${noun}${withWork ? ` (${withWork} with work)` : ''} · ${names}${blocked}`;
}

/**
 * 08 #8: the part of the save impact that should make you stop — finished work that a save would remove.
 * → { text (describeStructureImpact), warning ('189 sub-items with work will be removed' | ''), tone ('red' | 'amber' | null),
 *     removed, removedWithWork }
 */
export function structureImpactSummary(impact) {
  const textValue = describeStructureImpact(impact);
  if (!impact || impact.enabled === false) return { text: textValue, warning: '', tone: null, removed: 0, removedWithWork: 0 };
  const tasks = Array.isArray(impact.tasks) ? impact.tasks : [];
  const removed = tasks.reduce((sum, entry) => sum + (Number(entry.removed) || 0), 0);
  const removedWithWork = tasks.reduce((sum, entry) => sum + (Number(entry.removed_with_work) || 0), 0);
  const plural = (count) => `sub-item${count === 1 ? '' : 's'}`;
  if (impact.blocked) return { text: textValue, warning: `Save is refused: ${impact.blocked}`, tone: 'red', removed, removedWithWork };
  if (removedWithWork) {
    return { text: textValue, warning: `${removedWithWork} ${plural(removedWithWork)} with work will be removed`, tone: 'red', removed, removedWithWork };
  }
  if (removed) return { text: textValue, warning: `${removed} ${plural(removed)} will be removed`, tone: 'amber', removed, removedWithWork };
  return { text: textValue, warning: '', tone: null, removed, removedWithWork };
}

/**
 * 08 #4: ids of the containers whose subtree has an unsaved change (the change's node, or the parent it was added
 * to / removed from, and every ancestor of those) — the tree marks them with an amber dot.
 * `parentOf(id)` → the parent id in the CURRENT structure (null at the top).
 */
export function changedContainerIds(changes = [], parentOf = () => null) {
  const ids = new Set();
  const climb = (id) => {
    let current = id;
    const seen = new Set();
    while (current && !seen.has(current)) {
      seen.add(current);
      ids.add(current);
      current = parentOf(current);
    }
  };
  changes.forEach((change) => {
    if (change.type === 'removed' || change.type === 'added') climb(change.parentId);
    else climb(change.id || change.parentId);
    if (change.type === 'added' || change.type === 'moved') climb(change.id);
    if (change.fromParentId) climb(change.fromParentId);
  });
  return ids;
}
